import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useAuth } from '../context/AuthContext';
import { siteImages } from '../data/mock';
import { Mail, Lock, User, ArrowRight, UserPlus, AlertCircle, Eye, EyeOff, CheckCircle2 } from 'lucide-react';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', password: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    document.title = 'Create Account | Sport Fitt Pro';
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    login({ email: formData.email, firstName: formData.firstName, lastName: formData.lastName });
    navigate('/');
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />

      <main className="flex-1 pt-[104px] grid lg:grid-cols-2">
        {/* Left: Form Side */}
        <div className="flex items-center justify-center p-6 sm:p-10 lg:p-14 order-2 lg:order-1">
          <div className="w-full max-w-md">
            <div className="mb-8">
              <p className="text-athletic-500 text-[11px] font-black tracking-[0.4em] uppercase mb-3">Create Account</p>
              <h1 className="font-display text-5xl text-ink-900 tracking-wider leading-none mb-3">JOIN THE<br /><span className="text-athletic-500">MOVEMENT.</span></h1>
              <p className="text-ink-500">Sign up for early access, faster checkout and member-only drops.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-start gap-2 px-4 py-3 bg-athletic-50 border-2 border-athletic-100 text-athletic-700">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span className="text-sm font-medium">{error}</span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-black uppercase tracking-wider text-ink-900 mb-2">First Name</label>
                  <div className="relative">
                    <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
                    <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="John" className="w-full pl-11 pr-4 py-3.5 bg-ink-50 border-2 border-ink-100 focus:outline-none focus:border-athletic-500 text-ink-900 transition-colors" />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-black uppercase tracking-wider text-ink-900 mb-2">Last Name</label>
                  <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Doe" className="w-full px-4 py-3.5 bg-ink-50 border-2 border-ink-100 focus:outline-none focus:border-athletic-500 text-ink-900 transition-colors" />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-black uppercase tracking-wider text-ink-900 mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
                  <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="you@example.com" className="w-full pl-11 pr-4 py-3.5 bg-ink-50 border-2 border-ink-100 focus:outline-none focus:border-athletic-500 text-ink-900 transition-colors" />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-black uppercase tracking-wider text-ink-900 mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
                  <input type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleChange} placeholder="At least 6 characters" className="w-full pl-11 pr-11 py-3.5 bg-ink-50 border-2 border-ink-100 focus:outline-none focus:border-athletic-500 text-ink-900 transition-colors" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-ink-400 hover:text-athletic-500">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-black uppercase tracking-wider text-ink-900 mb-2">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
                  <input type={showPassword ? 'text' : 'password'} name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} placeholder="Confirm your password" className="w-full pl-11 pr-4 py-3.5 bg-ink-50 border-2 border-ink-100 focus:outline-none focus:border-athletic-500 text-ink-900 transition-colors" />
                </div>
              </div>

              <button type="submit" className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-athletic-500 hover:bg-athletic-600 text-white font-black uppercase tracking-wider transition-all">
                <UserPlus className="w-5 h-5" /> Create Account <ArrowRight className="w-4 h-4" />
              </button>
            </form>

            <div className="mt-8 pt-6 border-t-2 border-ink-100 text-center">
              <p className="text-ink-500 text-sm">
                Already a member?{' '}
                <Link to="/login" className="text-athletic-500 hover:text-athletic-600 font-black uppercase tracking-wider text-xs">Sign In</Link>
              </p>
            </div>
          </div>
        </div>

        {/* Right: Visual Side */}
        <div className="relative hidden lg:block bg-ink-950 overflow-hidden order-1 lg:order-2">
          <img src={siteImages.lifestyleGym || siteImages.heroMain} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-br from-ink-950/60 via-ink-950/70 to-athletic-950/60" />
          <div className="absolute inset-0 p-14 flex flex-col justify-between text-white">
            <div>
              <p className="text-athletic-400 text-[11px] font-black tracking-[0.4em] uppercase mb-3">Member Benefits</p>
              <h2 className="font-display text-6xl leading-none tracking-wider">LEVEL UP<br /><span className="text-athletic-500">FOR FREE.</span></h2>
            </div>
            <ul className="space-y-3">
              {['Early access to new 2025 drops','Exclusive member discounts','Free USA shipping on orders $75+','30-day no-hassle returns','Order tracking & support'].map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-athletic-400 mt-0.5 flex-shrink-0" />
                  <span className="text-white/80 text-sm">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default RegisterPage;
